// conventions-allow: file-size — this existing owner coordinates the DA scenario feature seams.
import { Tooltip } from '@mui/material'
import { useDatasets } from 'api/dataset/dataset.api'
import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import {
  useCopyBidsFromDate,
  useElplanBidCreateMutation,
  useElplanInfo,
  useElplanSendingBidMutation,
} from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import type { BidTypeName, ElectricityPriceScenario } from 'api/elplan/elplan.types'
import { postStat } from 'api/stats/stats.api'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import { getCalculatedDataset } from 'helpers/dataset.helper/dataset.calc.helper'
import { getDatasetInstructionsForSumVolumeCalc, getItemsForExcel } from 'helpers/elplan.helper/bidDataset.helper'
import { exportPlannedElectricityProductionToExcel } from 'helpers/elplan.helper/elplanExport.helper'
import { convertNumberDecimalSign, isArray } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { colors } from 'styles/variables'
import { Button, Card, Tabs } from 'ui/atoms'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { BidSubmitConfirmDialog } from 'ui/components/BidSubmitConfirmDialog/BidSubmitConfirmDialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import Datetime from 'utils/datetime/datetime'
import { translateKeyWithSpecificTranslations } from 'utils/stringUtils'
import { useSnapshot } from 'valtio'
import {
  getScenarioBidEditorPartitionId,
  resolveSelectedScenarioPartition,
} from 'views/DayAheadTradingView/DayAheadTradingView.helper'
import type {
  FormattedBid,
  FormattedBidWithIdAndTitle,
  RenderableScenarioPartitionResolution,
} from 'views/DayAheadTradingView/DayAheadTradingView.types'
import BidModal from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/BidModal/BidModal'
import CopyBidsModal from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/CopyBidsModal/CopyBidsModal'
import { useDayAheadBidTagSelectionContext } from 'views/ElectricityTradingView/components/shared/DayAheadBidEditor/useDayAheadBidTagSelectionContext'
import { confirmDayAheadBidTableLeave } from '../../dayAheadBidTableLeave.helper'
import { BidActivityBanner } from '../BidActivityBanner/BidActivityBanner'
import { ClearBidsAction } from '../ClearBidsAction/ClearBidsAction'
import { serializeDayAheadBidTableContext } from '../DayAheadBidTable/DayAheadBidTable.context.helper'
import type { DayAheadBidTableContext } from '../DayAheadBidTable/DayAheadBidTable.types'
import { useDayAheadBidTableResetController } from '../DayAheadBidTable/useDayAheadBidTableResetController'
import styles from './DayAheadPriceScenarios.module.less'
import ElectricityPriceScenarioModal from './ElectricityPriceScenarioModal/ElectricityPriceScenarioModal'
import ElectricityPriceScenarioVisualization from './ElectricityPriceScenarioVisualization/ElectricityPriceScenarioVisualization'
import { useHasBidsOutsideSelectedPartition } from './useHasBidsOutsideSelectedPartition'

type DayAheadPriceScenariosProps = {
  selectedPartitionKey: string
}

type BidCopyRequest = {
  bidType: BidTypeName
  partitionId?: number
}

export function DayAheadPriceScenarios({ selectedPartitionKey }: DayAheadPriceScenariosProps): ReactElement | null {
  const { t } = useTranslation()

  const auth = useAuth()
  const isSuperUser = auth.user?.is_superuser === true
  const elplanSnap = useSnapshot(elplanStore)
  const { data: elplanInfo, isPending } = useElplanInfo(auth.systemId)
  const [isBidConfirmOpen, setIsBidConfirmOpen] = useState(false)
  const clearResetController = useDayAheadBidTableResetController()

  const { mutate: createBid } = useElplanBidCreateMutation()
  const { mutate: copyBidsFromDate } = useCopyBidsFromDate()
  const { mutate: sendBidToElplan, isPending: isSendingBid } = useElplanSendingBidMutation()

  const primaryDigitalTwin = auth.activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  const [bidCopyRequest, setBidCopyRequest] = useState<BidCopyRequest>()
  const [currentBlockBid, setCurrentBlockBid] = useState<FormattedBidWithIdAndTitle>()
  const [currentProfileBid, setCurrentProfileBid] = useState<FormattedBidWithIdAndTitle>()
  const [openElectricityPriceModal, setOpenElectricityPriceModal] = useState(false)

  const selectedStartTime = elplanSnap.date.start_time ?? Datetime.getTomorrowDate().startTime
  const selectedEndTime = elplanSnap.date.end_time ?? Datetime.getTomorrowDate().endTime
  const selectedDate = {
    startTime: selectedStartTime,
    endTime: selectedEndTime,
  }

  const currentView = elplanSnap.date.current_view ?? 'tomorrow'

  const disabledBidButtons = currentView === 'optimizationPeriod'
  const disabledAddPriceButton = disabledBidButtons || !Datetime.isSameDate(selectedStartTime, selectedEndTime)

  const selectedPartitionId = elplanInfo?.partitions?.find(({ key }) => key === selectedPartitionKey)?.id
  function buildScenarioTableContextKey(
    scenario: ElectricityPriceScenario,
    partitionResolution: RenderableScenarioPartitionResolution
  ): string {
    const context: DayAheadBidTableContext = {
      elplanInfoId: elplanSnap.elplan_info_id,
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      gridIdentity: scenario.ui_config_id === null ? 'day_ahead_bid_management' : `ui-config:${scenario.ui_config_id}`,
      scenarioId: scenario.id,
      partitionId: getScenarioBidEditorPartitionId(partitionResolution),
    }
    return serializeDayAheadBidTableContext(context)
  }

  function getScenarioTableContextKey(scenario: ElectricityPriceScenario): string | undefined {
    const partitionResolution = resolveSelectedScenarioPartition({
      scenario,
      partitions: elplanInfo?.partitions ?? [],
      selectedPartitionKey,
    })
    if (partitionResolution.mode === 'error') {
      return undefined
    }

    return buildScenarioTableContextKey(scenario, partitionResolution)
  }

  function handleClearSuccess(contextKeys: string[], withdrawalId: string): void {
    for (const contextKey of contextKeys) {
      clearResetController.reset(contextKey, withdrawalId)
    }
  }
  const { tagsForDate, requireTagForBlockBids, blockBidInputCurrency, profileBidInputCurrency } =
    useDayAheadBidTagSelectionContext({ partitionId: selectedPartitionId })
  const sendIncludesBidsOutsideSelectedPartition = useHasBidsOutsideSelectedPartition({
    elplanInfoId: elplanSnap.elplan_info_id,
    startTime: selectedStartTime,
    endTime: resolveElplanBidsQueryEndTime(selectedEndTime),
    selectedPartitionId,
  })

  const systemHasBlockBids = elplanSnap.bid_types.some((bid) => bid.type === 'blockbid')
  const systemHasProfileBids = elplanSnap.bid_types.some((bid) => bid.type === 'profilebid')
  const amountOfBlockBids = elplanSnap.blockBidInfo.amount || 0
  const amountOfProfileBids = elplanSnap.profileBidInfo.amount || 0

  // Datasets for excel export of bids
  const onlyReturnIds = useMemo(() => {
    const onlyReturnIds = ['spotbid_without_price.volume']

    if (systemHasBlockBids) {
      for (let i = 0; i < amountOfBlockBids; i++) {
        onlyReturnIds.push(`blockbid.${i + 1}.volume`)
        onlyReturnIds.push(`blockbid.${i + 1}.price`)
      }
    }

    if (systemHasProfileBids) {
      for (let i = 0; i < amountOfProfileBids; i++) {
        onlyReturnIds.push(`profilebid.${i + 1}.volume`)
        onlyReturnIds.push(`profilebid.${i + 1}.price`)
      }
    }

    return onlyReturnIds
  }, [amountOfBlockBids, amountOfProfileBids, systemHasBlockBids, systemHasProfileBids])

  const { data: elplanBidsUiConfig } = useUiConfigAnchorComponent('elplan_bids')
  const { data: elplanBidsDatasets = [] } = useDatasets(
    elplanBidsUiConfig?.id ?? -1,
    elplanBidsUiConfig?.version ?? -1,
    elplanBidsUiConfig?.dataset_instructions || [],
    selectedStartTime,
    resolveElplanBidsQueryEndTime(selectedEndTime),
    {
      onlyReturnIds,
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
      datasetRefreshToken: ELPLAN_BID_REFRESH_EVENT_QUERY_KEY,
      overrideAlias: {
        elplan_info_id: elplanSnap.elplan_info_id,
      },
    }
  )

  const calcDatasets = getCalculatedDataset(
    elplanBidsDatasets,
    getDatasetInstructionsForSumVolumeCalc(amountOfBlockBids)
  )
  const maxBidVolume = elplanSnap?.maxBidVolume
  const bidsIsOvermaxBidVolume =
    typeof maxBidVolume === 'number' ? calcDatasets.values.some((num) => num && num > maxBidVolume) : false

  const itemsForExcel = getItemsForExcel(amountOfBlockBids)
  const itemsDataId: Set<string> = new Set(itemsForExcel.map((item) => item.data_id))
  const visibleDatasets = elplanBidsDatasets.filter(({ return_id }) => itemsDataId.has(return_id))
  const datasetNames = (itemsForExcel || []).reduce((acc, item) => ({ ...acc, [item.data_id]: item.title }), {})

  function getBidVolumeWithConvertedDecimalSign(bid: FormattedBid): number | number[] | undefined {
    if (isArray(bid.volume)) {
      return bid.volume.map((vol) => convertNumberDecimalSign(vol))
    }
    if (typeof bid.volume === 'number') {
      return convertNumberDecimalSign(bid.volume)
    }
    return bid.volume
  }

  function addNewBlockOrProfileBid(bid: FormattedBid): void {
    createBid({
      elplan_info: elplanSnap.elplan_info_id,
      tag: bid.tag,
      start_time: Datetime.toISOString(bid.startTime),
      end_time: Datetime.toISOString(bid.endTime),
      type: bid.type,
      volume: getBidVolumeWithConvertedDecimalSign(bid),
      price: bid.price ? convertNumberDecimalSign(bid.price) : undefined,
      name: bid.name || '-',
      electricity_price_scenario: bid.scenarioId,
      ...(bid.partitionId === undefined ? {} : { partition: bid.partitionId }),
      generate_block_order: true,
    })
  }

  function copySpotBidsFromDate(date: ISODateTime, { bidType, partitionId }: BidCopyRequest): void {
    postStat('copy-spotbids-from-date', `click`)

    setBidCopyRequest(undefined)
    copyBidsFromDate({
      elplan_info: elplanSnap.elplan_info_id,
      from_date: Datetime.toLocalTime(date, 'onlyDate') as ISODateTime,
      to_date: Datetime.toLocalTime(selectedStartTime, 'onlyDate') as ISODateTime,
      type: bidType,
      ...(partitionId === undefined ? {} : { partition: partitionId }),
    })
  }

  function closeBlockBidModal(): void {
    setCurrentBlockBid(undefined)
  }

  function closeProfileBidModal(): void {
    setCurrentProfileBid(undefined)
  }

  function renderScenarioVisualization(scenario: ElectricityPriceScenario): ReactElement {
    const partitionResolution = resolveSelectedScenarioPartition({
      scenario,
      partitions: elplanInfo?.partitions ?? [],
      selectedPartitionKey,
    })

    if (partitionResolution.mode === 'error') {
      return (
        <InfoBanner
          style="danger"
          text={t(
            'This scenario is not configured for the selected partition. Update its partition configuration in the administration interface.'
          )}
        />
      )
    }

    return (
      <ElectricityPriceScenarioVisualization
        scenario={scenario}
        tableContextKey={buildScenarioTableContextKey(scenario, partitionResolution)}
        partitions={elplanInfo?.partitions ?? []}
        partitionResolution={partitionResolution}
        datasetStartTime={selectedStartTime}
        datasetEndTime={selectedEndTime}
        setCurrentBlockBid={setCurrentBlockBid}
        setCurrentProfileBid={setCurrentProfileBid}
        setTypeOfBidToCopy={(bidType, partitionId) => setBidCopyRequest({ bidType, partitionId })}
        disabledBidButtons={disabledBidButtons}
        tagsForDate={tagsForDate}
        bidInputCurrencies={{ block: blockBidInputCurrency, profile: profileBidInputCurrency }}
        requireTagForBlockBids={requireTagForBlockBids}
        translations={digitalTwin?.translations}
        tableResetController={clearResetController}
      />
    )
  }

  if (!elplanInfo || isPending) {
    return null
  }
  if (elplanInfo.scenarios.length === 0) {
    return (
      <Card title={t(`Day ahead price scenarios`)}>
        <InfoBanner
          style={'warning'}
          text={t('No electricity price scenarios found. Please contact support to set up scenarios for your system.')}
        />
      </Card>
    )
  }

  return (
    <>
      <div>
        <Card title={t(`Day ahead price scenarios`)}>
          <div className={styles.DayAheadPriceScenarios_Period}>
            <div className={styles.DayAheadPriceScenarios_Period__right}>
              {elplanInfo.integration && auth.activeSystem && elplanInfo.supports_clear && (
                <ClearBidsAction
                  date={selectedStartTime}
                  systemName={auth.activeSystem.name}
                  systemId={auth.activeSystem.id}
                  isPreview={auth.activeSystem.is_preview}
                  isSuperUser={auth.user?.is_superuser === true}
                  disabled={disabledBidButtons}
                  clearContextKeys={(elplanInfo.scenarios ?? [])
                    .map(getScenarioTableContextKey)
                    .filter((contextKey): contextKey is string => contextKey !== undefined)}
                  onClearSuccess={handleClearSuccess}
                />
              )}
              {elplanInfo.integration && (
                <Tooltip
                  title={
                    bidsIsOvermaxBidVolume
                      ? t(`Some bids are over the maximum bid volume. Max volume for bids is {{maxBidVolume}} MW`, {
                          maxBidVolume: elplanSnap.maxBidVolume,
                        })
                      : ''
                  }
                  arrow
                >
                  <div className={styles.DayAheadPriceScenarios_SendBidsButtonContainer}>
                    <NewButton
                      variant="primary"
                      label={t('Send all bids')}
                      disabled={bidsIsOvermaxBidVolume || currentView === 'optimizationPeriod' || isSendingBid}
                      loading={isSendingBid}
                      onClick={() => {
                        if (!auth.activeSystem) {
                          return
                        }
                        if (isSuperUser) {
                          setIsBidConfirmOpen(true)
                        } else {
                          sendBidToElplan({
                            date: Datetime.toLocalTime(selectedStartTime, 'onlyDate'),
                            system: auth.activeSystem.name,
                            systemId: auth.activeSystem.id,
                          })
                        }
                      }}
                    />
                    {currentView !== 'optimizationPeriod' && (
                      <BidActivityBanner
                        elplanInfoId={elplanSnap.elplan_info_id}
                        startTime={selectedStartTime}
                        endTime={selectedEndTime}
                      />
                    )}
                  </div>
                </Tooltip>
              )}
              <TooltipIconButton
                icon="fas fa-cloud-download-alt"
                iconColor={colors.primary5}
                iconSize={'medium'}
                onClick={() => exportPlannedElectricityProductionToExcel(visibleDatasets, datasetNames, selectedDate)}
                tooltip={t('Export to Excel with 15 minute resolution')}
              />
            </div>
            {elplanInfo.scenarios.length > 1 && (
              <Button
                className={styles.DayAheadPriceScenarios_ElectricityPriceButton}
                secondary
                icon="fal fa-plus"
                disabled={disabledAddPriceButton}
                disabledTooltip={
                  disabledAddPriceButton ? t('Select a single day to add electricity prices') : undefined
                }
                onClick={() => setOpenElectricityPriceModal(true)}
              >
                {t('Add electricity price for scenarios')}
              </Button>
            )}
          </div>
          {(disabledBidButtons || sendIncludesBidsOutsideSelectedPartition) && (
            <div className="w-fit">
              <InfoBanner
                style="info"
                text={
                  disabledBidButtons
                    ? t('You cannot send bids or optimize scenarios in the optimization period view')
                    : t('Send includes bids outside the selected subnet that are not shown here')
                }
              />
            </div>
          )}
          {elplanInfo.scenarios.length === 1 && renderScenarioVisualization(elplanInfo.scenarios[0])}
          {(elplanInfo?.scenarios?.length ?? 0) > 1 && (
            <Tabs
              items={(elplanInfo?.scenarios ?? [])
                .sort((a, b) => a.scenario_number - b.scenario_number)
                .map((scenario) => ({
                  title: translateKeyWithSpecificTranslations(scenario.scenario_name, digitalTwin?.translations),
                  children: renderScenarioVisualization(scenario),
                }))}
              uniqueStorageKey={'elprice_scenario_tabs'}
              onBeforeChange={() => confirmDayAheadBidTableLeave('scenario')}
            />
          )}
        </Card>
        {bidCopyRequest !== undefined && (
          <CopyBidsModal
            onSubmit={(chosenDate) => copySpotBidsFromDate(chosenDate, bidCopyRequest)}
            closeModal={() => setBidCopyRequest(undefined)}
          />
        )}
        {currentBlockBid !== undefined && (
          <BidModal
            closeModal={closeBlockBidModal}
            bid={currentBlockBid}
            createBid={addNewBlockOrProfileBid}
            editBid={addNewBlockOrProfileBid}
            type={'block'}
            isEditing={currentBlockBid.fake}
            tagsForDate={tagsForDate}
            requireTagForBlockBids={requireTagForBlockBids}
            bidInputCurrency={blockBidInputCurrency}
          />
        )}
        {currentProfileBid !== undefined && (
          <BidModal
            closeModal={closeProfileBidModal}
            bid={currentProfileBid}
            createBid={addNewBlockOrProfileBid}
            editBid={addNewBlockOrProfileBid}
            type={'profile'}
            isEditing={currentProfileBid.fake}
            tagsForDate={tagsForDate}
            requireTagForBlockBids={requireTagForBlockBids}
            bidInputCurrency={profileBidInputCurrency}
          />
        )}
      </div>
      {openElectricityPriceModal && (
        <ElectricityPriceScenarioModal
          scenarios={elplanInfo?.scenarios ?? []}
          chosenDate={selectedDate}
          closeModal={() => setOpenElectricityPriceModal(false)}
        />
      )}
      {auth.activeSystem && (
        <BidSubmitConfirmDialog
          open={isBidConfirmOpen}
          onClose={() => setIsBidConfirmOpen(false)}
          onConfirm={() => {
            if (!auth.activeSystem) {
              return
            }
            setIsBidConfirmOpen(false)
            sendBidToElplan({
              date: Datetime.toLocalTime(selectedStartTime, 'onlyDate'),
              system: auth.activeSystem.name,
              systemId: auth.activeSystem.id,
            })
          }}
          systemName={auth.activeSystem.name}
          isPreview={auth.activeSystem.is_preview}
        />
      )}
    </>
  )
}
